import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Sunrise,
  Flag,
  Clock,
  AlertCircle,
  Cake,
  Lightbulb,
  ArrowRight,
  Bell,
} from "lucide-react";

import api from "@/lib/api";
import { formatDeDate } from "@/lib/dateFormat";

const PRIORITY_STYLE = {
  1: { label: "Eigene Zusage",   color: "bg-rose-50 text-rose-700 border-rose-200" },
  2: { label: "Heute fällig",    color: "bg-amber-50 text-amber-700 border-amber-200" },
  3: { label: "Jemand wartet",   color: "bg-blue-50 text-blue-700 border-blue-200" },
  4: { label: "Konkrete Chance", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  5: { label: "Guter Moment",    color: "bg-gray-50 text-gray-600 border-gray-200" },
};

const KIND_LABEL = {
  followup: "Wiedervorlage",
  task: "Aufgabe",
  challenge_next_step: "Nächster Schritt",
  waiting_overdue: "Zusage anderer",
};

const AMPEL_DOT = { gruen: "bg-emerald-500", gelb: "bg-amber-400", rot: "bg-rose-500" };

function Section({ title, icon: Icon, count, description, children, testid }) {
  return (
    <section className="crm-card p-5" data-testid={testid}>
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          <h2 className="font-semibold text-gray-800">{title}</h2>
          {typeof count === "number" ? (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{count}</span>
          ) : null}
        </div>
      </header>
      {description ? <p className="text-xs text-gray-500 mb-3">{description}</p> : null}
      {children}
    </section>
  );
}

function Empty({ children }) {
  return (
    <div className="text-sm text-gray-400 italic text-center py-6">
      {children}
    </div>
  );
}

export default function MyDay() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["my-day"],
    queryFn: async () => (await api.get(`/my-day`)).data,
    refetchOnWindowFocus: true,
  });

  if (isLoading || !data) {
    return <div className="p-8 text-sm text-gray-500" data-testid="my-day-loading">Lädt „Mein Tag"…</div>;
  }

  const todayHuman = formatDeDate(data.today);

  return (
    <div data-testid="my-day-page" className="space-y-5 pb-8">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-widest">
            <Sunrise className="w-4 h-4" />
            <span>Mein Tag</span>
          </div>
          <h1
            className="font-display font-bold tracking-tight text-2xl md:text-3xl text-gray-900 mt-1"
            data-testid="my-day-headline"
          >
            {todayHuman}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Kalenderwoche {data.iso_week} · Wo willst du starten?
          </p>
        </div>
      </div>

      {/* A. Heute wichtig */}
      <Section
        title="Heute wichtig"
        icon={AlertCircle}
        count={data.important.length}
        description="Sortierung: verbindliche Zusagen → Fristen → wartet auf Rückmeldung. Du entscheidest, womit du beginnst."
        testid="my-day-section-important"
      >
        {data.important.length === 0 ? (
          <Empty>Nichts drängt heute. Guter Moment für Beziehungspflege.</Empty>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.important.map((it) => {
              const style = PRIORITY_STYLE[it.priority_rank] || PRIORITY_STYLE[5];
              const kindLabel = KIND_LABEL[it.kind] || it.kind;
              return (
                <li
                  key={`${it.kind}-${it.id}`}
                  className="py-3 flex items-start gap-3"
                  data-testid={`my-day-important-${it.id}`}
                >
                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium border shrink-0 ${style.color}`}>
                    {style.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{it.title}</span>
                      <span className="text-[11px] uppercase tracking-wider text-gray-500">{kindLabel}</span>
                      {it.overdue ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 font-semibold">
                          <AlertCircle className="w-3 h-3" /> Rückmeldung prüfen · überfällig
                        </span>
                      ) : null}
                    </div>
                    {it.description ? (
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{it.description}</p>
                    ) : null}
                    <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap items-center gap-2">
                      {it.due_date ? <span>Fällig: {formatDeDate(it.due_date)}</span> : null}
                      {it.contact_name ? (
                        <button
                          onClick={() => it.contact_id && navigate(`/contacts/${it.contact_id}`)}
                          className="hover:text-gray-900"
                        >
                          {it.contact_name}
                        </button>
                      ) : null}
                      {it.company_name ? <span>· {it.company_name}</span> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* A2. Diese Woche fällig – Wiedervorlagen späterer Tage dieser KW (Phase 4) */}
      {(data.followups_this_week || []).length > 0 ? (
        <Section
          title="Diese Woche fällig"
          icon={Bell}
          count={data.followups_this_week.length}
          description="Wiedervorlagen, die später in dieser Kalenderwoche fällig sind – nicht heute, aber nah dran."
          testid="my-day-section-week-followups"
        >
          <ul className="space-y-2">
            {data.followups_this_week.map((it) => (
              <li
                key={it.id}
                className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-lg p-3"
                data-testid={`my-day-week-followup-${it.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 leading-tight">{it.title}</div>
                  <div className="mt-1 text-xs text-gray-600 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-blue-700 font-medium">Fällig {formatDeDate(it.due_date)}</span>
                    {it.contact_name ? (
                      <button
                        type="button"
                        onClick={() => it.contact_id && navigate(`/contacts/${it.contact_id}`)}
                        className="hover:underline"
                      >
                        · {it.contact_name}
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* B. Diese Woche aktiv */}
      <Section
        title="Diese Woche aktiv"
        icon={Flag}
        count={data.active_challenges.length}
        description="Herausforderungen, die du in der Wochenplanung aktiv angehen möchtest."
        testid="my-day-section-active"
      >
        {data.active_challenges.length === 0 ? (
          <Empty>Noch keine Herausforderung als „diese Woche aktiv" eingeordnet.</Empty>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.active_challenges.map(({ challenge: ch, contacts }) => (
              <div
                key={ch.id}
                className="rounded-lg border border-gray-200 bg-white p-3"
                data-testid={`my-day-active-${ch.id}`}
              >
                <div className="font-semibold text-gray-900">{ch.titel}</div>
                {ch.ziel ? <p className="text-xs text-gray-600 mt-1 line-clamp-2">{ch.ziel}</p> : null}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-xs text-gray-700">
                  <div>
                    <div className="uppercase tracking-wider text-[10px] text-gray-500">Nächster Schritt</div>
                    <div>{ch.naechster_schritt || <span className="text-gray-400 italic">—</span>}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-[10px] text-gray-500">Nächster Termin</div>
                    <div>{ch.naechster_termin ? formatDeDate(ch.naechster_termin) : <span className="text-gray-400 italic">—</span>}</div>
                  </div>
                </div>
                {contacts?.length ? (
                  <div className="mt-2 text-[11px] text-gray-500">
                    Beteiligte: {contacts.join(", ")}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* C. Wartet auf andere */}
      <Section
        title="Wartet auf andere"
        icon={Clock}
        count={data.waiting.length}
        description="Offene Zusagen von anderer Seite – kein Vertriebsdruck, nur transparent."
        testid="my-day-section-waiting"
      >
        {data.waiting.length === 0 ? (
          <Empty>Aktuell wartest du auf niemanden.</Empty>
        ) : (
          <ul className="space-y-3">
            {data.waiting.map(({ challenge: ch, contacts, overdue }) => (
              <li
                key={ch.id}
                className={`rounded-lg border p-3 ${overdue ? "border-rose-200 bg-rose-50" : "border-gray-200 bg-white"}`}
                data-testid={`my-day-waiting-${ch.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{ch.titel}</span>
                      {overdue ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200 font-medium">
                          <AlertCircle className="w-3 h-3" /> Rückmeldung prüfen
                        </span>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-xs text-gray-700">
                      <div>
                        <div className="uppercase tracking-wider text-[10px] text-gray-500">Worauf wird gewartet</div>
                        <div>{ch.naechster_schritt || ch.ziel || <span className="text-gray-400 italic">—</span>}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-[10px] text-gray-500">Von wem</div>
                        <div>
                          {contacts?.length ? contacts.join(", ") : <span className="text-gray-400 italic">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-[10px] text-gray-500">Ursprüngliche Frist</div>
                        <div className={overdue ? "text-rose-700 font-medium" : ""}>
                          {ch.naechster_termin ? formatDeDate(ch.naechster_termin) : <span className="text-gray-400 italic">—</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* D. Persönliche Anlässe */}
      <Section
        title="Persönliche Anlässe"
        icon={Cake}
        count={data.birthdays.length}
        description="Geburtstage in den nächsten sieben Tagen."
        testid="my-day-section-birthdays"
      >
        {data.birthdays.length === 0 ? (
          <Empty>
            Keine bevorstehenden Anlässe. Sobald bei einer Person ein Geburtstag hinterlegt ist,
            erscheint er hier automatisch.
          </Empty>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.birthdays.map((b) => (
              <li
                key={b.contact_id}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`my-day-birthday-${b.contact_id}`}
              >
                <div>
                  <div className="font-medium text-gray-900">{b.contact_name}</div>
                  <div className="text-xs text-gray-500">
                    {b.days_until === 0
                      ? "Heute!"
                      : b.days_until === 1
                        ? "Morgen"
                        : `In ${b.days_until} Tagen`}
                    {" · "}{formatDeDate(b.birthday)}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/contacts/${b.contact_id}`)}
                  className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  Personenakte <ArrowRight className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* E. Ideen vom Vortag */}
      <Section
        title="Ideen vom Vortag"
        icon={Lightbulb}
        count={data.ideas_from_yesterday.length}
        description="Was möchtest du daraus machen?"
        testid="my-day-section-ideas"
      >
        {data.ideas_from_yesterday.length === 0 ? (
          <Empty>
            Noch keine Ideen von gestern. Sobald eine Idee priorisiert wird, erscheint sie hier
            am nächsten Tag.
          </Empty>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.ideas_from_yesterday.map((i) => (
              <li key={i.id} className="py-3" data-testid={`my-day-idea-${i.id}`}>
                <div className="font-medium text-gray-900">{i.title}</div>
                {i.description ? (
                  <p className="text-xs text-gray-600 mt-0.5">{i.description}</p>
                ) : null}
                <div className="text-[11px] text-gray-500 mt-1">
                  Was möchtest du daraus machen?
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
